/**
 * @fullvsl/app-shell/views/integrations — Full Funnel self-service connection
 * screen (FUL-254 / FUL-128 / FUL-247).
 *
 * Each subaccount admin connects their own Full Funnel location by
 * supplying an API key / private-integration token they control. The secret is
 * submitted once to the backend vault (AES-256-GCM at rest) and never persisted
 * client-side — the input is cleared immediately on submit and only the masked
 * `RedactedCredencial` is ever rendered.
 *
 * States handled (FUL-128 §2.1):
 *  - empty / no connections → connect form
 *  - connected → masked credential, verified scopes, test/rotate/revoke
 *  - error → redacted message + missing-scope guidance, rotate/revoke
 *  - revoked → reconnect
 *
 * Iframe-safe: no top-window access, no third-party cookies. Detects the FF
 * context `locationId` to pre-fill (best-effort) when embedded.
 */
import type {
  ConnectionStatus,
  ConnectionTestResult,
  CredentialAuditEvent,
  TenantConnection,
} from "../../../contracts/src/contracts";
import { REQUIRED_FF_SCOPES } from "../../../contracts/src/contracts";
import type { ConnectionStore } from "../connection-store";
import { initConnectionStore } from "../connection-store";
import { el, append, field, fmtDate, icon, ICONS } from "../dom";

export function renderIntegrationsView(): { mount: HTMLElement; cleanup: () => void } {
  const wrapper = el("div");
  let disposed = false;

  void render(wrapper);

  async function render(root: HTMLElement): Promise<void> {
    if (disposed) return;
    const store = await initConnectionStore();

    root.innerHTML = "";

    // ---- Page header ----
    const header = el("div", "fva-page-header");
    const headerText = el("div");
    append(headerText, el("h2", null, "Integrações"));
    append(
      headerText,
      el("p", null, "Conecte o Full Funnel ou envie eventos das VSLs para outros sistemas."),
    );
    append(header, headerText);
    append(root, header);

    const tabs = el("div", "fva-tabs fva-integ-tabs");
    const fullFunnelTab = el("button", "fva-tab is-active", "Full Funnel") as HTMLButtonElement;
    const webhooksTab = el("button", "fva-tab", "Webhooks") as HTMLButtonElement;
    fullFunnelTab.type = "button";
    webhooksTab.type = "button";
    append(tabs, fullFunnelTab, webhooksTab);
    append(root, tabs);

    const fullFunnelPanel = el("div");
    const webhooksPanel = el("div");
    webhooksPanel.style.display = "none";
    append(root, fullFunnelPanel, webhooksPanel);

    const selectPanel = (selected: "fullfunnel" | "webhooks") => {
      const fullFunnelAtivo = selected === "fullfunnel";
      fullFunnelTab.classList.toggle("is-active", fullFunnelAtivo);
      webhooksTab.classList.toggle("is-active", !fullFunnelAtivo);
      fullFunnelPanel.style.display = fullFunnelAtivo ? "block" : "none";
      webhooksPanel.style.display = fullFunnelAtivo ? "none" : "block";
    };
    fullFunnelTab.addEventListener("click", () => selectPanel("fullfunnel"));
    webhooksTab.addEventListener("click", () => selectPanel("webhooks"));

    // ---- Self-service banner ----
    const banner = el("div", "fva-info-banner");
    append(banner, el("strong", null, "Suas credenciais permanecem privadas."));
    banner.appendChild(document.createTextNode(" "));
    banner.appendChild(
      document.createTextNode(
        "Cada administrador informa sua própria credencial. Não existe uma conta compartilhada do Full Funnel. " +
          "As chaves são criptografadas e não voltam a ser exibidas após a conexão.",
      ),
    );
    append(fullFunnelPanel, banner);

    // ---- Layout: main column + side ----
    const grid = el("div", "fva-integ-grid");
    const mainCol = el("div", "fva-integ-main");
    const sideCol = el("div", "fva-integ-side");
    sideCol.appendChild(renderPermissionsCard());
    append(grid, mainCol, sideCol);
    append(fullFunnelPanel, grid);
    append(webhooksPanel, renderWebhooksPanel());

    // Connect form is rendered once; only the list below refreshes, so the
    // submit feedback (success/error banner) persists after a connect/rotate.
    const formHost = el("div");
    const listHost = el("div", "fva-integ-list");
    append(mainCol, formHost, listHost);

    // Latest list snapshot, read by the form for rotation-hint detection.
    let latest: TenantConnection[] = [];
    const refreshList = async () => {
      if (disposed) return;
      listHost.innerHTML = "";
      try {
        latest = await store.list();
      } catch {
        append(listHost, renderError("Não foi possível carregar as conexões. Tente novamente."));
        return;
      }
      if (latest.length === 0) {
        const empty = el("div", "fva-empty fva-integ-empty");
        append(empty, el("h3", null, "Nenhuma localização conectada"));
        append(empty, el("p", null, "Use o formulário acima para conectar sua primeira localização do Full Funnel."));
        append(listHost, empty);
        return;
      }
      append(listHost, el("h3", "fva-form-section-title", "Localizações conectadas"));
      for (const conn of latest) {
        append(listHost, await renderConnectionCard(store, conn, refreshList));
      }
    };

    append(formHost, renderConnectForm(store, () => latest, refreshList));
    await refreshList();
  }

  return {
    mount: wrapper,
    cleanup: () => {
      disposed = true;
    },
  };
}

/* ---------------------------------------------------------------------------------
 * Connect form
 * --------------------------------------------------------------------------------- */

function renderConnectForm(
  store: ConnectionStore,
  getExisting: () => TenantConnection[],
  onChange: () => void | Promise<void>,
): HTMLElement {
  const card = el("div", "fva-card fva-card-pad");
  append(card, el("h3", "fva-publish-section-title", "Conectar uma localização"));

  const form = el("form", "fva-form");
  form.addEventListener("submit", (e) => e.preventDefault());

  const locationIdInput = el("input", "fva-input") as HTMLInputElement;
  locationIdInput.type = "text";
  locationIdInput.placeholder = "loc_xxxxxxxx";
  locationIdInput.required = true;
  locationIdInput.value = detectIframeLocation();
  append(form, field("ID da localização", locationIdInput, "A localização do Full Funnel à qual esta credencial pertence."));

  const secretInput = el("input", "fva-input") as HTMLInputElement;
  secretInput.type = "password";
  secretInput.placeholder = "pit-… (token de integração privada / chave de API)";
  secretInput.required = true;
  secretInput.autocomplete = "off";
  append(form, field("Chave de API / token", secretInput, "Enviada uma única vez, criptografada e não exibida novamente."));

  const nameInput = el("input", "fva-input") as HTMLInputElement;
  nameInput.type = "text";
  nameInput.placeholder = "Ex.: Empresa Exemplo";
  append(form, field("Nome da localização (opcional)", nameInput));

  // Rotating an existing location? Surface that context.
  const hint = el("p", "fva-vsl-meta");
  const updateHint = () => {
    const existingIds = new Set(getExisting().map((c) => c.locationId));
    hint.textContent =
      existingIds.has(locationIdInput.value.trim())
        ? "Esta localização já está conectada. O envio substituirá a credencial."
        : "";
  };
  locationIdInput.addEventListener("input", updateHint);
  append(form, hint);

  // Status / feedback line
  const status = el("div");
  status.style.minHeight = "20px";
  append(form, status);

  const submitBtn = el("button", "fva-btn fva-btn-primary", "Conectar e testar") as HTMLButtonElement;
  submitBtn.type = "submit";
  append(form, submitBtn);

  form.addEventListener("submit", async () => {
    const locationId = locationIdInput.value.trim();
    const secret = secretInput.value;
    if (!locationId || !secret) return;
    setFormStatus(status, "info", "Conectando…");
    submitBtn.disabled = true;
    try {
      const name = nameInput.value.trim() || undefined;
      const conn = await store.connect({ locationId, secret, locationName: name });
      if (conn.status === "connected") {
        setFormStatus(status, "success", `Conectada: todas as permissões foram verificadas para ${conn.locationName ?? locationId}.`);
      } else {
        setFormStatus(status, "error", conn.lastError ?? "O teste falhou. Verifique a credencial e as permissões.");
      }
      // Clear the secret immediately — never retained client-side.
      secretInput.value = "";
      updateHint();
      onChange();
    } catch (err) {
      setFormStatus(status, "error", redactedError(err));
    } finally {
      submitBtn.disabled = false;
    }
  });

  append(card, form);
  return card;
}

/* ---------------------------------------------------------------------------------
 * Per-location connection card
 * --------------------------------------------------------------------------------- */

async function renderConnectionCard(
  store: ConnectionStore,
  conn: TenantConnection,
  onChange: () => void,
): Promise<HTMLElement> {
  const card = el("div", "fva-card fva-card-pad fva-integ-conn");

  // Header: name + status badge
  const head = el("div", "fva-integ-conn-head");
  const headLeft = el("div", "fva-integ-conn-head-left");
  append(headLeft, statusDot(conn.status));
  const titleWrap = el("div");
  append(titleWrap, el("h4", "fva-integ-conn-title", conn.locationName ?? `Localização ${conn.locationId.slice(-6)}`));
  append(titleWrap, el("span", "fva-vsl-meta fva-mono", conn.locationId));
  append(headLeft, titleWrap);
  append(head, headLeft);
  append(head, statusBadge(conn.status));
  append(card, head);

  // Masked credential + lifecycle timestamps
  const meta = el("div", "fva-integ-conn-meta");
  append(meta, metaCell("Credencial", conn.credential.masked, true));
  if (conn.connectedAt) append(meta, metaCell("Conectada", fmtDate(conn.connectedAt)));
  if (conn.rotatedAt) append(meta, metaCell("Substituída", fmtDate(conn.rotatedAt)));
  if (conn.lastTestedAt) append(meta, metaCell("Último teste", fmtDate(conn.lastTestedAt)));
  if (conn.revokedAt) append(meta, metaCell("Revogada", fmtDate(conn.revokedAt)));
  append(card, meta);

  // Scope chips
  append(card, renderPermissions(conn));

  // Ausente-scope guidance (error path)
  if (conn.missingScopes.length > 0) {
    append(card, renderMissingPermissionsGuidance(conn.missingScopes));
  }

  // Last error banner
  if (conn.lastError && conn.status === "error") {
    const err = el("div", "fva-error-banner");
    append(err, el("strong", null, "Erro de conexão: "));
    err.appendChild(document.createTextNode(conn.lastError));
    append(card, err);
  }

  // Histórico de auditoria (live mode only — mock has no events)
  const audit = await safeAudit(store, conn.locationId);
  if (audit.length > 0) {
    append(card, renderAuditTrail(audit));
  }

  // Actions
  const actions = el("div", "fva-integ-conn-actions");
  const statusLine = el("span", "fva-vsl-meta");
  append(actions, statusLine);

  if (conn.status === "connected" || conn.status === "error") {
    const testBtn = el("button", "fva-btn fva-btn-secondary fva-btn-sm", "Testar conexão") as HTMLButtonElement;
    testBtn.type = "button";
    testBtn.addEventListener("click", async () => {
      testBtn.disabled = true;
      statusLine.textContent = "Testando…";
      try {
        const res: ConnectionTestResult = await store.test(conn.locationId, { force: true });
        statusLine.textContent = res.ok ? "Todas as permissões verificadas ✓" : (res.message ?? "Teste falhou");
      } catch (err) {
        statusLine.textContent = redactedError(err);
      } finally {
        testBtn.disabled = false;
        onChange();
      }
    });
    append(actions, testBtn);
  }

  const revokeBtn = el("button", "fva-btn fva-btn-danger fva-btn-sm", "Revogar") as HTMLButtonElement;
  revokeBtn.type = "button";
  revokeBtn.addEventListener("click", async () => {
    if (!confirm(`Revogar a conexão de ${conn.locationName ?? conn.locationId}? A credencial será apagada.`)) return;
    revokeBtn.disabled = true;
    statusLine.textContent = "Revogando…";
    try {
      await store.revoke(conn.locationId);
      statusLine.textContent = "Credencial revogada.";
    } catch (err) {
      statusLine.textContent = redactedError(err);
    } finally {
      revokeBtn.disabled = false;
      onChange();
    }
  });
  append(actions, revokeBtn);
  append(card, actions);

  return card;
}

/* ---------------------------------------------------------------------------------
 * Permissões
 * --------------------------------------------------------------------------------- */

function renderPermissionsCard(): HTMLElement {
  const card = el("div", "fva-card fva-card-pad");
  append(card, el("h3", "fva-publish-section-title", "Permissões necessárias"));
  append(card, el("p", "fva-vsl-meta", "A credencial do Full Funnel precisa conceder estas permissões."));
  const list = el("div", "fva-scope-chips");
  for (const scope of REQUIRED_FF_SCOPES) {
    append(list, scopeChip(scope, true));
  }
  append(card, list);
  return card;
}

function renderPermissions(conn: TenantConnection): HTMLElement {
  const wrap = el("div", "fva-integ-scopes");
  append(wrap, el("span", "fva-label", "Permissões"));
  const chips = el("div", "fva-scope-chips");
  const present = new Set(conn.scopes);
  for (const scope of REQUIRED_FF_SCOPES) {
    append(chips, scopeChip(scope, present.has(scope)));
  }
  append(wrap, chips);
  return wrap;
}

function scopeChip(scope: string, ok: boolean): HTMLElement {
  const chip = el("span", ok ? "fva-scope-chip is-ok" : "fva-scope-chip is-missing");
  append(chip, el("span", "fva-scope-chip-dot"));
  chip.appendChild(document.createTextNode(scope));
  chip.title = ok ? "Verificada" : "Ausente";
  return chip;
}

function renderMissingPermissionsGuidance(missing: string[]): HTMLElement {
  const banner = el("div", "fva-error-banner fva-integ-scope-help");
  append(banner, el("strong", null, "Permissões necessárias ausentes"));
  const list = el("ul", "fva-scope-missing-list");
  for (const scope of missing) {
    append(list, el("li", "fva-mono", scope));
  }
  append(banner, list);
  const note = el("p", "fva-vsl-meta");
  note.style.margin = "8px 0 0";
  note.textContent = "Autorize novamente a chave no Full Funnel com estas permissões e conecte-a outra vez.";
  append(banner, note);
  return banner;
}

/* ---------------------------------------------------------------------------------
 * Webhooks externos
 * --------------------------------------------------------------------------------- */

export const WEBHOOK_EVENTS = [
  { id: "play", label: "Reprodução" },
  { id: "milestone", label: "Marcos assistidos" },
  { id: "pause", label: "Pausa" },
  { id: "cta_shown", label: "CTA exibida" },
  { id: "cta_clicked", label: "CTA clicada" },
  { id: "completed", label: "Concluída" },
  { id: "resume_accepted", label: "Retomada aceita" },
] as const;

export type OutboundWebhookEvent = (typeof WEBHOOK_EVENTS)[number]["id"];

interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: OutboundWebhookEvent[];
  active: boolean;
  createdAt: string;
  lastTestedAt?: string;
  lastTestStatus?: "success" | "error";
}

const WEBHOOK_STORAGE_KEY = "fullvsl:integration:webhooks";

export function normalizeWebhookUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function buildWebhookTestPayload(endpointId: string, at = new Date().toISOString()) {
  return {
    id: `test_${endpointId}_${at}`,
    event: "fullvsl.test",
    occurredAt: at,
    source: "fullvsl",
    data: { message: "Teste de entrega de webhook do FullVSL" },
  } as const;
}

function loadWebhookEndpoints(): WebhookEndpoint[] {
  try {
    const raw = localStorage.getItem(WEBHOOK_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as WebhookEndpoint[];
    if (!Array.isArray(parsed)) return [];
    const allowed = new Set(WEBHOOK_EVENTS.map((event) => event.id));
    return parsed.filter((endpoint) =>
      endpoint &&
      typeof endpoint.id === "string" &&
      typeof endpoint.name === "string" &&
      normalizeWebhookUrl(endpoint.url) !== null &&
      Array.isArray(endpoint.events) &&
      endpoint.events.every((event) => allowed.has(event))
    );
  } catch {
    return [];
  }
}

function saveWebhookEndpoints(endpoints: WebhookEndpoint[]): void {
  try {
    localStorage.setItem(WEBHOOK_STORAGE_KEY, JSON.stringify(endpoints));
  } catch {
    // Storage may be partitioned inside an iframe; the current session still works.
  }
}

function renderWebhooksPanel(): HTMLElement {
  const panel = el("div", "fva-webhook-panel");
  let endpoints = loadWebhookEndpoints();
  let feedback: { kind: "success" | "error"; message: string } | null = null;

  const render = () => {
    panel.innerHTML = "";

    const intro = el("div", "fva-integ-section-head");
    append(intro, el("h3", null, "Webhooks externos"));
    append(intro, el("p", null, "Envie eventos selecionados das VSLs para automações, CRMs, plataformas de dados ou seu próprio endpoint."));
    append(panel, intro);

    if (feedback) {
      const banner = el("div", feedback.kind === "success" ? "fva-success-banner" : "fva-error-banner");
      banner.textContent = feedback.message;
      append(panel, banner);
    }

    const layout = el("div", "fva-webhook-grid");
    append(layout, renderWebhookForm(), renderWebhookList());
    append(panel, layout);
  };

  const renderWebhookForm = (): HTMLElement => {
    const card = el("div", "fva-card fva-card-pad");
    append(card, el("h3", "fva-publish-section-title", "Adicionar endpoint"));
    const form = el("form", "fva-form");
    form.addEventListener("submit", (event) => event.preventDefault());

    const nameInput = el("input", "fva-input") as HTMLInputElement;
    nameInput.required = true;
    nameInput.placeholder = "Ex.: Automação de vendas";
    append(form, field("Nome", nameInput));

    const urlInput = el("input", "fva-input") as HTMLInputElement;
    urlInput.required = true;
    urlInput.type = "url";
    urlInput.placeholder = "https://example.com/webhooks/fullvsl";
    append(form, field("URL do endpoint", urlInput, "É obrigatório usar HTTPS."));

    const eventGrid = el("div", "fva-webhook-events");
    for (const event of WEBHOOK_EVENTS) {
      const label = document.createElement("label");
      label.className = "fva-check-row";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.value = event.id;
      input.checked = event.id === "cta_clicked" || event.id === "completed";
      label.append(input, document.createTextNode(event.label));
      append(eventGrid, label);
    }
    append(form, field("Eventos", eventGrid, "Escolha pelo menos um evento."));

    const submit = el("button", "fva-btn fva-btn-primary", "Adicionar webhook") as HTMLButtonElement;
    submit.type = "submit";
    append(form, submit);

    form.addEventListener("submit", () => {
      const url = normalizeWebhookUrl(urlInput.value);
      const events = [...eventGrid.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked')]
        .map((input) => input.value as OutboundWebhookEvent);
      if (!nameInput.value.trim() || !url || events.length === 0) {
        feedback = { kind: "error", message: "Informe um nome, um endpoint HTTPS válido e pelo menos um evento." };
        render();
        return;
      }
      const id = globalThis.crypto?.randomUUID?.() ?? `wh_${Date.now().toString(36)}`;
      endpoints = [{
        id,
        name: nameInput.value.trim(),
        url,
        events,
        active: true,
        createdAt: new Date().toISOString(),
      }, ...endpoints];
      saveWebhookEndpoints(endpoints);
      feedback = { kind: "success", message: "Webhook adicionado. Envie um evento de teste para verificar o endpoint." };
      render();
    });

    append(card, form);
    return card;
  };

  const renderWebhookList = (): HTMLElement => {
    const list = el("div", "fva-webhook-list");
    if (endpoints.length === 0) {
      const empty = el("div", "fva-empty fva-webhook-empty");
      append(empty, el("h3", null, "Nenhum endpoint de webhook"));
      append(empty, el("p", null, "Adicione um endpoint para começar a enviar eventos das VSLs."));
      append(list, empty);
      return list;
    }
    for (const endpoint of endpoints) append(list, renderWebhookEndpoint(endpoint));
    return list;
  };

  const renderWebhookEndpoint = (endpoint: WebhookEndpoint): HTMLElement => {
    const card = el("div", "fva-card fva-card-pad fva-webhook-endpoint");
    const head = el("div", "fva-integ-conn-head");
    const title = el("div");
    append(title, el("h4", "fva-integ-conn-title", endpoint.name));
    append(title, el("span", "fva-vsl-meta fva-webhook-url", endpoint.url));
    append(head, title, el("span", `fva-badge ${endpoint.active ? "fva-badge-live" : "fva-badge-revoked"}`, endpoint.active ? "Ativo" : "Pausado"));
    append(card, head);

    const chips = el("div", "fva-scope-chips");
    for (const event of endpoint.events) append(chips, el("span", "fva-scope-chip is-ok", WEBHOOK_EVENTS.find((item) => item.id === event)?.label ?? event));
    append(card, chips);

    if (endpoint.lastTestedAt) {
      append(card, el("p", "fva-vsl-meta", `Último teste: ${endpoint.lastTestStatus === "success" ? "entregue" : "falhou"} · ${fmtDate(endpoint.lastTestedAt)}`));
    }

    const actions = el("div", "fva-integ-conn-actions");
    const testButton = el("button", "fva-btn fva-btn-secondary fva-btn-sm", "Enviar teste") as HTMLButtonElement;
    testButton.type = "button";
    testButton.disabled = !endpoint.active;
    testButton.addEventListener("click", async () => {
      testButton.disabled = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(endpoint.url, {
          method: "POST",
          headers: { "content-type": "application/json", "x-fullvsl-event": "fullvsl.test" },
          body: JSON.stringify(buildWebhookTestPayload(endpoint.id)),
          signal: controller.signal,
        });
        endpoint.lastTestStatus = response.ok ? "success" : "error";
        feedback = response.ok
          ? { kind: "success", message: `Test entregue to ${endpoint.name}.` }
          : { kind: "error", message: `O endpoint retornou HTTP ${response.status}.` };
      } catch {
        endpoint.lastTestStatus = "error";
        feedback = { kind: "error", message: "Não foi possível acessar o endpoint ou ele não permitiu a solicitação de teste." };
      } finally {
        clearTimeout(timeout);
        endpoint.lastTestedAt = new Date().toISOString();
        saveWebhookEndpoints(endpoints);
        render();
      }
    });

    const toggleButton = el("button", "fva-btn fva-btn-secondary fva-btn-sm", endpoint.active ? "Pausar" : "Ativar") as HTMLButtonElement;
    toggleButton.type = "button";
    toggleButton.addEventListener("click", () => {
      endpoint.active = !endpoint.active;
      saveWebhookEndpoints(endpoints);
      feedback = { kind: "success", message: `${endpoint.name} ${endpoint.active ? "ativado" : "pausado"}.` };
      render();
    });

    const deleteButton = el("button", "fva-btn fva-btn-danger fva-btn-sm", "Excluir") as HTMLButtonElement;
    deleteButton.type = "button";
    deleteButton.addEventListener("click", () => {
      if (!confirm(`Excluir o webhook "${endpoint.name}"?`)) return;
      endpoints = endpoints.filter((item) => item.id !== endpoint.id);
      saveWebhookEndpoints(endpoints);
      feedback = { kind: "success", message: "Webhook excluído." };
      render();
    });
    append(actions, testButton, toggleButton, deleteButton);
    append(card, actions);
    return card;
  };

  render();
  return panel;
}

/* ---------------------------------------------------------------------------------
 * Histórico de auditoria
 * --------------------------------------------------------------------------------- */

function renderAuditTrail(events: CredentialAuditEvent[]): HTMLElement {
  const wrap = el("div", "fva-integ-audit");
  append(wrap, el("span", "fva-label", "Histórico de auditoria"));
  const list = el("div", "fva-event-log");
  const sorted = [...events].sort((a, b) => (a.at < b.at ? 1 : -1)).slice(0, 12);
  for (const ev of sorted) {
    const row = el("div", "fva-event-row");
    append(row, el("span", "fva-event-name", ev.action.replace("_", " ")));
    const metaSpan = el("span", "fva-event-meta");
    metaSpan.appendChild(document.createTextNode(`${fmtDate(ev.at)} — ${ev.detail ?? ev.status}`));
    append(row, metaSpan);
    append(list, row);
  }
  append(wrap, list);
  return wrap;
}

/* ---------------------------------------------------------------------------------
 * Small helpers
 * --------------------------------------------------------------------------------- */

function statusBadge(status: ConnectionStatus): HTMLElement {
  const map: Record<ConnectionStatus, { cls: string; label: string }> = {
    connected: { cls: "fva-badge-live", label: "Conectada" },
    pending: { cls: "fva-badge-draft", label: "Pendente" },
    error: { cls: "fva-badge-error", label: "Erro" },
    revoked: { cls: "fva-badge-revoked", label: "Revogada" },
  };
  const s = map[status];
  return el("span", `fva-badge ${s.cls}`, s.label);
}

function statusDot(status: ConnectionStatus): HTMLElement {
  const dot = el("span", "fva-integ-dot");
  const on = status === "connected";
  const off = status === "revoked" || status === "pending";
  dot.classList.add(on ? "fva-integ-dot-on" : off ? "fva-integ-dot-off" : "fva-integ-dot-err");
  return dot;
}

function metaCell(label: string, value: string, mono = false): HTMLElement {
  const cell = el("div", "fva-integ-meta-cell");
  append(cell, el("span", "fva-integ-meta-label", label));
  append(cell, el("span", mono ? "fva-mono fva-integ-meta-value" : "fva-integ-meta-value", value));
  return cell;
}

function renderError(message: string): HTMLElement {
  const banner = el("div", "fva-error-banner");
  banner.textContent = message;
  return banner;
}

function setFormStatus(node: HTMLElement, kind: "info" | "success" | "error", message: string): void {
  node.innerHTML = "";
  const cls =
    kind === "error" ? "fva-error-banner" : kind === "success" ? "fva-success-banner" : "fva-info-banner";
  const banner = el("div", cls);
  if (kind === "success") banner.appendChild(icon(ICONS.checkCircle, "fva-integ-check"));
  banner.appendChild(document.createTextNode(message));
  node.appendChild(banner);
}

/**
 * Best-effort detection of the FF iframe locationId for pre-fill (FUL-128 §4.2/§9.1).
 *
 * The app shell runs inside a Full Funnel Custom Menu Link iframe.
 * Full Funnel passes the active subaccount context via query parameters on the iframe
 * URL (the documented merge-token mechanism, FUL-128 §9.1 — verified at the gate).
 * This reads the known candidate param names so the ID da localização field is
 * pre-filled regardless of which convention the real host uses. It stays strictly
 * query-param based — no parent-window DOM access — and degrades to an empty
 * string inside a sandboxed iframe where `location.search` is unavailable.
 */
const FF_LOCATION_QUERY_PARAMS = ["location", "locationId", "location_id", "loc"];

/**
 * Resolve a Full Funnel location id from a query string. Pure so it
 * is unit-testable without DOM manipulation; `detectIframeLocation` feeds it the
 * live `window.location.search`.
 */
export function locationIdFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of FF_LOCATION_QUERY_PARAMS) {
    const value = params.get(key);
    if (value) return value;
  }
  return "";
}

function detectIframeLocation(): string {
  try {
    return locationIdFromSearch(window.location.search);
  } catch {
    // location.search may be unavailable in a sandboxed iframe.
  }
  return "";
}

async function safeAudit(store: ConnectionStore, locationId: string): Promise<CredentialAuditEvent[]> {
  try {
    return await store.audit(locationId);
  } catch {
    return [];
  }
}

/** Map any thrown error to a safe, redacted message (no token fragments). */
function redactedError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/401|403|unauthorized|forbidden/i.test(msg)) return "A autorização falhou. Sua sessão pode ter expirado.";
  if (/404|not found/i.test(msg)) return "Conexão não encontrada. Ela pode ter sido revogada.";
  if (/501|not configured/i.test(msg)) return "O cofre de credenciais não está configurado.";
  return "A solicitação de conexão falhou. Tente novamente.";
}
